import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataRefsByKey, Domain, Language, LanguagesByKey, LocalizedKeyValue, Market, ProfilePerimeter } from '@shared/models/dataref.model';
import { ConfigService } from '@services/config.service';
import { NotificationService } from '@services/notification.service';
import { RefDataService } from '@services/ref-data.service';
import { TextPopinComponent } from '@shared/components/text-popin/text-popin.component';
import {
  AppliancesListByDomainMarket,
  ClientConfiguration,
  EventJSONVersion,
  MarketClientConfiguration,
  MarketClientConfigurationKnownProperties,
  ServicesConfs,
  UIDomainConfigurationWithCapacity,
} from '../../models/client.model';
import { CopyVersionDialogComponent } from '../copy-version-dialog/copy-version-dialog.component';
import { CreateEditVersionDialogComponent } from '../create-edit-version-dialog/create-edit-version-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, ValidationErrors } from '@angular/forms';

@Component({
  selector: 'app-clients-versions-form',
  templateUrl: './clients-versions-form.component.html',
  styleUrls: ['./clients-versions-form.component.scss'],
  standalone: false,
})
export class ClientsVersionsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup;
  @Input() displayedVersion: string | null = null;
  @Input() allRestrictedFields: string[] = [];

  domainsList: Domain[] = [];
  domainsObject: DataRefsByKey<Domain> = {};
  marketsList: Market[] = [];
  languagesList: Language[] = [];
  profilePerimetersList: LocalizedKeyValue[] = [];
  profilePerimetersObject: DataRefsByKey<ProfilePerimeter> = {};
  applicationsList: LocalizedKeyValue[] = [];
  countriesList: LocalizedKeyValue[] = [];
  unitsList: LocalizedKeyValue[] = [];
  allAppliances: AppliancesListByDomainMarket = {};
  marketLang: any = {};

  APP_KEY = 'CLIENTNATURE_APP';
  WEB_KEY = 'CLIENTNATURE_WEB';
  ADD = 'add';
  EDIT = 'edit';
  DELETE = 'delete';

  // subject used to unsubscribe to avoid memory leaks on component destruction
  private readonly destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private configService: ConfigService,
    public dialog: MatDialog,
    private refDataService: RefDataService,
  ) {
    this.allRestrictedFields = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.form && changes.form.currentValue) {
      this.updateAppliancesList('', '');
      this.checkEmptyErrors();
      this.updateMarketLang();
    }
  }

  ngOnInit(): void {
    this.refDataService
      .getByKey('domains', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allDomains) => {
        this.domainsList = this.refDataService.translateReference(this.refDataService.toArray(allDomains));
        this.domainsObject = allDomains;
      });

    this.refDataService
      .getByKey('markets', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allMarkets) => {
        this.marketsList = this.refDataService.translateReference(this.refDataService.toArray(allMarkets));
        this.updateMarketLang();
      });

    this.refDataService
      .getByKey('languages', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allLanguages) => {
        this.languagesList = this.refDataService.translateReference(this.refDataService.toArray(allLanguages));
      });

    this.refDataService
      .getByKey('profilePerimeters', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allProfilePerimeters) => {
        this.profilePerimetersList = this.refDataService.translateReference(this.refDataService.toArray(allProfilePerimeters));
        this.profilePerimetersObject = allProfilePerimeters;
      });

    this.refDataService
      .getByKey('applications', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allApplications) => {
        this.applicationsList = this.refDataService.translateReference(this.refDataService.toArray(allApplications));
      });

    this.refDataService
      .getByKey('countries', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allCountries) => {
        this.countriesList = this.refDataService.translateReference(this.refDataService.toArray(allCountries));
        // sort by alphabet
        this.countriesList.sort((a: any, b: any) => a.translatedName.localeCompare(b.translatedName));
      });

    this.refDataService
      .getByKey('units', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allUnits) => {
        this.unitsList = this.refDataService.translateReference(this.refDataService.toArray(allUnits));
      });
  }

  getMarketLabel(marketKey: string): string {
    const market = this.marketsList.find((m) => m.key === marketKey);
    if (market) {
      return market.translatedName + ' (' + market.key + ')';
    }
    return '';
  }

  addVersion(): void {
    const data: any = {
      isEdit: false,
    };
    const dialogRef = this.dialog.open(CreateEditVersionDialogComponent, {
      data,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.checkIsVersionExist(result)) {
          this.notificationService.sendError(this.translateService.instant('APPLICATIONS.ERROR_NAME_VERSION'));
        } else {
          const newVersionConfiguration: ClientConfiguration = {
            version: result,
            availableDomains: [],
            availableMarkets: [],
            markets: [],
            mustUpgrade: false,
            shouldUpgrade: false,
          };

          this.form.controls.configurations.setValue(
            this.form.controls.configurations.value
              ? [...this.form.controls.configurations.value, newVersionConfiguration]
              : [newVersionConfiguration],
          );
          this.displayedVersion = result;
        }
      }
    });
  }

  updateDigitalServiceForm(services: { marketName: string; servicesConfs: Partial<ServicesConfs> }): void {
    const configurations: ClientConfiguration[] = this.form.controls.configurations.value;
    const currentConfiguration = configurations.find((conf: ClientConfiguration) => conf.version === this.displayedVersion);

    if (currentConfiguration) {
      const actualMarketIndex = currentConfiguration.markets.findIndex(
        (market: MarketClientConfiguration) => market.name === services.marketName,
      );

      if (actualMarketIndex !== -1) {
        const market = currentConfiguration.markets[actualMarketIndex];

        // the client may not have been set up with any service until now and must be initialized
        if (!market.servicesConfs) {
          market.servicesConfs = {
            isEnabled: false,
            digitalServices: [],
          };
        }

        market.servicesConfs.isEnabled = !!services.servicesConfs.isEnabled;
        if (services.servicesConfs.digitalServices) {
          market.servicesConfs.digitalServices = services.servicesConfs.digitalServices;
        }
      }
    }
  }

  onServicesFormStatusChange(servicesConfsInvalid: boolean): void {
    if (servicesConfsInvalid) {
      this.form.controls.configurations.setErrors({ invalid: true });
    } else {
      this.form.controls.configurations.setErrors(null);
    }
  }

  changeVersion(): void {
    setTimeout(() => {
      this.marketChange(this.findCurrentVersionConfiguration(), '');
      this.checkEmptyErrors();
    }, 1);
  }

  importVersion(): void {
    const data: any = {
      list: this.form.controls.configurations.value.filter((conf: any) => conf.version !== this.displayedVersion),
    };
    const dialogRef = this.dialog.open(CopyVersionDialogComponent, {
      data,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          const configurations = this.form.controls.configurations.value;
          const versionToImport = configurations.find((conf: { version: string | null }) => conf.version === result);
          const deepClonedVersion = JSON.parse(JSON.stringify(versionToImport));

          // Ensure deepClonedVersion has the correct version
          deepClonedVersion.version = this.displayedVersion;

          const updatedConfigurations = configurations.map((conf: { version: string | null }) => {
            if (conf.version === this.displayedVersion) {
              // Replace with deep cloned version
              return deepClonedVersion;
            }
            return conf;
          });

          this.form.controls.configurations.setValue(updatedConfigurations);
        }
      });
  }

  editVersionName(): void {
    const data: any = {
      isEdit: true,
      version: this.displayedVersion,
    };
    const dialogRef = this.dialog.open(CreateEditVersionDialogComponent, {
      data,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          if (this.checkIsVersionExist(result)) {
            this.notificationService.sendError(this.translateService.instant('APPLICATIONS.ERROR_NAME_VERSION'));
          } else {
            this.form.controls.configurations.setValue(
              this.form.controls.configurations.value.map((conf: any) => {
                if (conf.version === this.displayedVersion) {
                  conf.version = result;
                }
                return conf;
              }),
            );
            if (this.displayedVersion === this.form.controls.currentVersion.value) {
              this.form.controls.currentVersion.setValue(result);
            }
            this.displayedVersion = result;
          }
        }
      });
  }

  deleteVersion(): void {
    const data: any = {
      text: this.translateService.instant('APPLICATIONS.POPINDELETE').replace('%(version)s', this.displayedVersion),
      cancelBtn: this.translateService.instant('CANCEL'),
      validateBtn: this.translateService.instant('DELETE'),
    };
    const dialogRef = this.dialog.open(TextPopinComponent, {
      data,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.form.controls.configurations.setValue(
            this.form.controls.configurations.value.filter((conf: any) => conf.version !== this.displayedVersion),
          );
          this.displayedVersion = this.form.controls.configurations.value[0].version;
        }
      });
  }

  checkIsVersionExist(version: string): boolean {
    if (!this.form.controls.configurations.value) {
      return false;
    }
    return !!this.form.controls.configurations.value.find((conf: any) => conf.version === version);
  }

  setVersionDefault(): void {
    this.form.controls.currentVersion.setValue(this.displayedVersion);
  }

  findCurrentVersionConfiguration(): ClientConfiguration | null {
    if (!this.form.controls.configurations.value) {
      return null;
    }
    return this.form.controls.configurations.value.find((obj: any) => this.displayedVersion === obj.version);
  }

  findCurrentIndexVersion(): any {
    if (!this.form.controls.configurations.value) {
      return null;
    }
    return this.form.controls.configurations.value.findIndex((obj: any) => this.displayedVersion === obj.version);
  }

  changeJSONVersion(event: EventJSONVersion): void {
    switch (event.action) {
    case this.ADD:
      this.form.controls.configurations.value[this.findCurrentIndexVersion()].markets.forEach(
        (market: any) => (market[event.key] = event.value),
      );
      this.form.controls.configurations.patchValue(JSON.parse(JSON.stringify(this.form.controls.configurations.value)));
      break;
    case this.EDIT:
      this.form.controls.configurations.value[this.findCurrentIndexVersion()].markets.forEach((market: any) => {
        if (event.oldKey && typeof market[event.oldKey] !== typeof event.value) {
          // if new type
          if (typeof market[event.key] !== typeof event.value) {
            // prevent to delete old value
            market[event.key] = event.value;
          }
        }
        if (event.key !== event.oldKey && event.oldKey) {
          // if new key
          if (typeof market[event.oldKey] === typeof event.value && event.oldKey in market) {
            // if it has old value copy him
            market[event.key] = market[event.oldKey];
          }
          delete market[event.oldKey];
        }
        return market;
      });
      this.form.controls.configurations.patchValue(JSON.parse(JSON.stringify(this.form.controls.configurations.value)));
      break;
    case this.DELETE:
      this.form.controls.configurations.value[this.findCurrentIndexVersion()].markets.forEach((market: any) => delete market[event.key]);
      this.form.controls.configurations.patchValue(JSON.parse(JSON.stringify(this.form.controls.configurations.value)));
      break;
    }
    this.checkEmptyErrors();
    this.updateMarketLang();
  }

  removeException(market: MarketClientConfiguration, indexException: number): void {
    market.exceptions.splice(indexException, 1);
  }

  addException(marketConfiguration: MarketClientConfiguration): void {
    if (!marketConfiguration.exceptions) {
      marketConfiguration.exceptions = [];
    }
    marketConfiguration.exceptions.push({ country: '', lang: '', rcuLang: '' });
  }

  // ****   UPDATE FUNCTIONS **** //
  // **************************** //

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  marketChange(currentVersion: any, event: any): void {
    // Remove markets that are not available in availableMarkets
    currentVersion.markets = (currentVersion.markets ?? []).filter((market: any) =>
      currentVersion.availableMarkets.some((availableMarket: any) => availableMarket === market.name),
    );
    currentVersion.availableMarkets.forEach((marketKey: any) => {
      // check add market
      if (!currentVersion.markets.some((market: any) => market.name === marketKey)) {
        const market: MarketClientConfigurationKnownProperties = {
          name: marketKey,
          exceptions: [],
          availableLangs: [],
          availableDomains: [],
          domains: [],
          profilePerimeter: '',
          rcuBaseUrl: '',
          rcuBrand: '',
          rcuMarket: '',
          servicesConfs: { isEnabled: false, digitalServices: [] },
          isAccountMandatory: false,
        };
        if (currentVersion.markets[0]) {
          // clone other market fields
          Object.keys(currentVersion.markets[0]).forEach((key) => {
            if (!this.allRestrictedFields.includes(key)) {
              switch (this.typeOf(currentVersion.markets[0][key])) {
              case 'list':
                 
                // @ts-ignore
                market[key] = [];
                break;
              case 'boolean':
                 
                // @ts-ignore
                market[key] = false;
                break;
              case 'number':
                 
                // @ts-ignore
                market[key] = 0;
                break;
              default:
                 
                // @ts-ignore
                market[key] = '';
              }
            }
          });
        }

        currentVersion.markets.push(market);
      }
    });
    this.updateAppliancesList('', event);
    this.updateMarketLang();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  domainChange(event: any): void {
    this.updateAppliancesList(event, '');
  }

  // **** copy ****//
  copyToMarket(name: string, field: string, market: MarketClientConfiguration): void {
    // if no configuration, do nothing (should not happen if we pass in this block of code)
    const currentConfiguration = this.findCurrentVersionConfiguration();
    if (!currentConfiguration) {
      return;
    }

    const newObject = {} as MarketClientConfiguration; // create object
    switch (field) {
    case 'ALL':
      Object.keys(market).forEach((key) => {
        if (!this.allRestrictedFields.includes(key)) {
          newObject[key] = market[key];
        }
      });
      newObject.profilePerimeter = market.profilePerimeter;
      newObject.rcuBaseUrl = market.rcuBaseUrl;
      newObject.rcuMarket = market.rcuMarket;
      newObject.rcuBrand = market.rcuBrand;
      newObject.domains = market.domains;
      newObject.availableDomains = market.availableDomains;
      newObject.domainsWithApplianceCapacity = market.domainsWithApplianceCapacity;
      newObject.exceptions = market.exceptions;
      break;
    case 'SPECIFIC_FIELDS':
      Object.keys(market).forEach((key) => {
        if (!this.allRestrictedFields.includes(key)) {
          newObject[key] = market[key];
        }
      });
      break;
    case 'profilePerimeter':
      newObject.profilePerimeter = market.profilePerimeter;
      newObject.rcuBaseUrl = market.rcuBaseUrl;
      newObject.rcuMarket = market.rcuMarket;
      newObject.rcuBrand = market.rcuBrand;
      break;
    case 'SERVICE_MANAGEMENT':
      if (market.servicesConfs) {
        newObject.servicesConfs = JSON.parse(JSON.stringify(market.servicesConfs));
      }
      break;

    default:
      // copy specific field
      newObject[field] = market[field];
    }

    if (name === '') {
      // copy on all
      currentConfiguration.markets = currentConfiguration.markets.map((marketConfiguration: MarketClientConfiguration) => ({
        ...marketConfiguration,
        ...newObject,
      }));
    } else {
      // copy on one
      const marketIndex = currentConfiguration.markets.findIndex(
        (marketConfiguration: MarketClientConfiguration) => name === marketConfiguration.name,
      );
      currentConfiguration.markets[marketIndex] = { ...currentConfiguration.markets[marketIndex], ...newObject };
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  typeOf(obj: any): string {
    if (obj === null) {
      return 'number';
    }
    return typeof obj === 'object' ? 'list' : typeof obj;
  }

  /**
   * Check if the given client nature can manage domain's activation status by market. (Display of the domain-activation-form component)
   * Only APP and WEB client can.
   *
   * @returns true if client nature can manage it, false otherwise
   */
  canManageActivatedDomainsByMarket(clientNatureKey?: null | string): boolean {
    return clientNatureKey === this.WEB_KEY || clientNatureKey === this.APP_KEY;
  }

  updateProfile(market: MarketClientConfiguration): void {
    const profile = this.profilePerimetersObject[market.profilePerimeter];
    if (profile.rcuBaseUrl != null) {
      market.rcuBaseUrl = profile.rcuBaseUrl;
    }
    if (profile.rcuMarket != null) {
      market.rcuMarket = profile.rcuMarket;
    }
    if (profile.rcuBrand != null) {
      market.rcuBrand = profile.rcuBrand;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateMarketAvailableDomains(): void {
    const currentConfiguration = this.findCurrentVersionConfiguration();

    if (this.canManageActivatedDomainsByMarket(this.form.controls.nature?.value) && currentConfiguration && currentConfiguration.markets) {
      currentConfiguration.markets.forEach((marketClientConfiguration: MarketClientConfiguration, index: number) => {
        const globalAvailableDomains = currentConfiguration.availableDomains;

        const marketDomainsWithApplianceCapacity = marketClientConfiguration.domainsWithApplianceCapacity ?? [];

        // add available domains
        globalAvailableDomains.forEach((domainKey: string) => {
          // change domainsWithApplianceCapacity to add domains on which it is not activated, for display purpose
          if (
            !marketDomainsWithApplianceCapacity.some(
              (domainConfigurationWithCapacity: UIDomainConfigurationWithCapacity) =>
                domainConfigurationWithCapacity.domainKey === domainKey,
            )
          ) {
            if (Object.keys(this.allAppliances).some((appDomain: string) => appDomain === domainKey)) {
              marketDomainsWithApplianceCapacity.push({
                domainKey,
                defaultApplianceId: null,
                isPersonalizedAdaptationEnabled: false,
                isUgcEnabled: false,
                isActive: false,
              });
            }
          }
        });

        // remove domains which may have been deleted from available domains
        marketDomainsWithApplianceCapacity.forEach((domainConfigurationWithCapacity: UIDomainConfigurationWithCapacity, index2: number) => {
          if (globalAvailableDomains.indexOf(domainConfigurationWithCapacity.domainKey) === -1) {
            marketDomainsWithApplianceCapacity.splice(index2, 1);
          }
        });

        marketDomainsWithApplianceCapacity.forEach((domainConfigurationWithCapacity: UIDomainConfigurationWithCapacity) => {
          domainConfigurationWithCapacity.isActive = !!domainConfigurationWithCapacity.defaultApplianceId;
        });
        marketClientConfiguration.domainsWithApplianceCapacity = marketDomainsWithApplianceCapacity;
        currentConfiguration.markets[index] = marketClientConfiguration;
      });
    }
  }

  checkEmptyErrors(): void {
    // check manually all the error in markets
    setTimeout(() => {
      if (this.form.controls.configurations.value) {
        const errorList: ValidationErrors[] = [];

        this.form.controls.configurations.value.forEach((conf: ClientConfiguration) => {
          if (conf.markets) {
            conf.markets.forEach((marketConfiguration: MarketClientConfiguration, marketIndex: number) => {
              this.whenEmptySetError(marketConfiguration.profilePerimeter, 'profilePerimeter', marketIndex, errorList);
              this.whenEmptySetError(marketConfiguration.defaultLang, 'defaultLang', marketIndex, errorList);
              this.whenEmptySetError(marketConfiguration.defaultCountry, 'defaultCountry', marketIndex, errorList);
              (marketConfiguration.domainsWithApplianceCapacity ?? []).forEach(
                (uiDomainConfiguration: UIDomainConfigurationWithCapacity, domainConfigurationIndex: number) => {
                  if (uiDomainConfiguration.isActive && !uiDomainConfiguration.defaultApplianceId) {
                    const errorKey = 'Empty' + marketIndex + domainConfigurationIndex + 'DefaultAppliance' + domainConfigurationIndex;
                    const error: ValidationErrors = { [errorKey]: true };
                    errorList.push(error);
                  }
                },
              );
            });
          }
        });
        this.form.controls.configurations.setErrors(null);
        errorList.forEach((error: ValidationErrors) => this.form.controls.configurations.setErrors(error));
      }
    }, 10);
  }
  private whenEmptySetError(field: any, nameField: string, marketIndex: number, errorList: ValidationErrors[]): void {
    if (!field || (field && field.lenght === 0)) {
      const errorKey = 'empty' + marketIndex + nameField;
      const error: ValidationErrors = { [errorKey]: true };
      errorList.push(error);
    }
  }

  // get and update Appliance List for dropdown
  private updateAppliancesList(domainChanged: string, marketChanged: string): void {
    const currentConfiguration = this.findCurrentVersionConfiguration();

    // if no current config found or there is no available domains in the current config, do nothing
    if (!(currentConfiguration && currentConfiguration.availableDomains)) {
      return;
    }

    if (domainChanged !== '' && marketChanged !== '') {
      this.updateAppliancesListFromDomainMarket(domainChanged, marketChanged);
    } else if (domainChanged !== '') {
      currentConfiguration.availableMarkets.forEach((marketKey: string) => {
        this.updateAppliancesListFromDomainMarket(domainChanged, marketKey);
      });
    } else if (marketChanged !== '') {
      currentConfiguration.availableDomains.forEach((domainKey: string) => {
        this.updateAppliancesListFromDomainMarket(domainKey, marketChanged);
      });
    } else {
      currentConfiguration.availableDomains.forEach((domainKey: string) => {
        currentConfiguration.availableMarkets.forEach((marketKey: string) => {
          this.updateAppliancesListFromDomainMarket(domainKey, marketKey);
        });
      });
    }
  }

  private updateAppliancesListFromDomainMarket(domainKey: string, marketKey: string): void {
    this.refDataService
      .getByKey('appliances', true, domainKey, marketKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((appliancesResult) => {
        this.allAppliances[domainKey] = this.allAppliances[domainKey] || {};
        //Reset the list for only domainKey and marketKey to avoid duplicates
        this.allAppliances[domainKey][marketKey] = [];

        Object.keys(appliancesResult).forEach((key) => {
          if (typeof appliancesResult[key].name[marketKey] !== 'undefined') {
            this.allAppliances[domainKey][marketKey].push(appliancesResult[key]);
          }
        });
        this.updateMarketAvailableDomains();
      });
  }

  private updateMarketLang(): void {
    this.marketLang = {};
    this.form.controls.configurations.value?.forEach((version: any) => {
      version.markets?.forEach((market: any) => {
        this.marketLang[market.name] = this.findLangByMarket(market.name);
      });
    });
  }
  private findLangByMarket(marketName: any): any[] {
    const market = this.marketsList.find((m: any) => marketName === m.key);
    let list = this.refDataService.toArray(market ? (market.langs as LanguagesByKey) : {});
    list = list.map((data: any) => {
      const tr = this.refDataService.innerTranslate(data.name);
      return { ...data, translatedName: tr[this.configService.currentLanguage] ?? tr.en };
    });
    return list;
  }
}
